import Icon from "../../../components/AppIcon";
import type { MaintenanceRequest } from "../../../store/api/maintenanceApi";

interface MaintenanceStatsProps {
	requests: MaintenanceRequest[];
}

const MaintenanceStats: React.FC<MaintenanceStatsProps> = ({ requests }) => {
	const calculateStats = () => {
		const stats = {
			total: requests.length,
			pending: 0,
			inProgress: 0,
			completed: 0,
			urgent: 0,
			high: 0,
		};

		requests.forEach((request) => {
			if (request.status === "pending") stats.pending++;
			if (request.status === "in_progress") stats.inProgress++;
			if (request.status === "completed") stats.completed++;
			if (request.priority === "urgent") stats.urgent++;
			if (request.priority === "high") stats.high++;
		});

		return stats;
	};

	const stats = calculateStats();

	const StatCard = ({
		icon,
		title,
		count,
		color,
		bgColor,
	}: {
		icon: string;
		title: string;
		count: number;
		color: "success" | "warning" | "error" | "primary";
		bgColor: string;
	}) => (
		<div className={`p-3 rounded-lg ${bgColor} overflow-hidden`}>
			<div className="flex items-center space-x-2 mb-2 gap-2">
				<div
					className={`p-1.5 rounded flex-shrink-0 ${
						color === "success"
							? "bg-success/10"
							: color === "warning"
							? "bg-warning/10"
							: color === "error"
							? "bg-error/10"
							: "bg-primary/10"
					}`}
				>
					<Icon
						name={icon as any}
						size={14}
						className={
							color === "success"
								? "text-success"
								: color === "warning"
								? "text-warning"
								: color === "error"
								? "text-error"
								: "text-primary"
						}
					/>
				</div>
				<h4 className="text-xs font-medium text-foreground truncate min-w-0 flex-1">{title}</h4>
			</div>

			<div className="min-w-0">
				<p className="text-base sm:text-lg font-bold text-foreground truncate">{count}</p>
			</div>
		</div>
	);

	return (
		<div className="bg-card border border-border rounded-lg card-shadow">
			<div className="p-3 sm:p-4 border-b border-border">
				<h3 className="text-sm sm:text-base font-semibold text-foreground">Statistics</h3>
				<p className="text-xs sm:text-sm text-muted-foreground mt-1">
					Current period overview
				</p>
			</div>

			<div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
				{/* Total Requests */}
				<StatCard
					icon="Wrench"
					title="Total Requests"
					count={stats.total}
					color="primary"
					bgColor="bg-primary/5"
				/>

				{/* Pending */}
				<StatCard
					icon="Clock"
					title="Pending"
					count={stats.pending}
					color="warning"
					bgColor="bg-warning/5"
				/>

				{/* In Progress */}
				<StatCard
					icon="Wrench"
					title="In Progress"
					count={stats.inProgress}
					color="primary"
					bgColor="bg-primary/5"
				/>

				{/* Completed */}
				<StatCard
					icon="CheckCircle"
					title="Completed"
					count={stats.completed}
					color="success"
					bgColor="bg-success/5"
				/>

				{/* Urgent */}
				<StatCard
					icon="AlertTriangle"
					title="Urgent"
					count={stats.urgent}
					color="error"
					bgColor="bg-error/5"
				/>
			</div>

			{/* Completion Rate */}
			{requests.length > 0 && (
				<div className="p-4 border-t border-border">
					<div className="flex items-center justify-between mb-2">
						<span className="text-sm font-medium text-foreground">
							Completion Rate
						</span>
						<span className="text-sm font-bold text-foreground">
							{((stats.completed / stats.total) * 100).toFixed(1)}%
						</span>
					</div>

					<div className="w-full bg-muted rounded-full h-2">
						<div
							className="bg-success h-2 rounded-full smooth-transition"
							style={{
								width: `${(stats.completed / stats.total) * 100}%`,
							}}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default MaintenanceStats;
